import { Children, useContext, type PropsWithChildren } from 'react';
import styled, { useTheme } from 'styled-components';
import { modeColor } from '../../styles';
import { Button } from '../Button';
import { HStack, Stack } from '../Core';
import { FormControlSizes } from '../Form/types';
import { Kbd } from '../Kbd';
import { Spinner } from '../Spinner';
import { Text } from '../Text';
import { PowerSearchActiveGroupContext } from './PowerSearchActiveGroupContext';

export function PowerSearchGroup({
  title,
  isLoading,
  maxItems = 5,
  useContentExpansion = false,
  groupId,
  children,
  ...props
}: PropsWithChildren<{
  title: string;
  isLoading: boolean;
  maxItems?: number;
  groupId: string;
  /**
   * When set, Show more toggles this value instead of revealing extra child rows.
   * The caller decides how collapsed content is clipped.
   */
  useContentExpansion?: boolean;
}>) {
  const { baseSize, fontSizeDefault } = useTheme();
  const { activeGroupId, expandedGroupIds, toggleExpandedGroupId, beforeGroupToggle } =
    useContext(PowerSearchActiveGroupContext);
  const isActive = activeGroupId !== '' && activeGroupId === groupId;
  const hasShowMore = useContentExpansion || Children.count(children) > maxItems;
  const isExpanded = expandedGroupIds.has(groupId);

  return (
    <Stack alignItems="stretch" data-testid="power-search-group" data-group-id={groupId} {...props}>
      <GroupHeader>
        <HStack fontSize="fontSizeTiny" gap="spacing8">
          <Text transform="uppercase">{title}</Text>
          {isLoading && <Spinner size={baseSize * fontSizeDefault} />}
        </HStack>
        {hasShowMore && (
          <ShowMoreArea gap="spacing4" $isActive={isActive} data-testid="power-search-show-more-area">
            {isActive && (
              <HStack gap="spacing4" fontSize="fontSizeTiny" data-testid="power-search-shift-enter-hint">
                <Kbd>⇧⏎</Kbd>
              </HStack>
            )}
            <Button
              size={FormControlSizes.Tiny}
              ghost={true}
              onClick={() => {
                beforeGroupToggle(groupId);
                toggleExpandedGroupId(groupId);
              }}
              data-testid="power-search-show-more-button"
            >
              {isExpanded ? 'Show less' : 'Show more'}
            </Button>
          </ShowMoreArea>
        )}
      </GroupHeader>
      {useContentExpansion
        ? children
        : Children.map(children, (child, index) => (isExpanded || index < maxItems ? child : null))}
    </Stack>
  );
}

const GroupHeader = styled(HStack).attrs({
  px: 'spacing16',
  h: 24,
  justifyContent: 'space-between',
  position: 'sticky',
  top: '0',
  zIndex: 1,
})`
  background: ${({ theme }) => modeColor(theme, theme.colors.gray['000'], theme.colors.gray['040'])};
  border-top: 1px solid ${({ theme }) => modeColor(theme, theme.colors.black.mute, theme.colors.gray['000'])};
`;

const ShowMoreArea = styled(HStack)<{ $isActive: boolean }>`
  opacity: ${({ $isActive }) => ($isActive ? 1 : 0.4)};
`;
